import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { DxToolbarComponent } from 'devextreme-angular';
import { CoreBotRequest } from '../../../models/core-bot-request';
import { NbButton } from '@nebular/theme';
import * as events from 'devextreme/events';
import { SellerService } from '../../../services/seller.service';
import { CoreBotService } from '../../../services/core-bot.service';
import { CoreBotPopupContentComponent } from '../corebot-shared/popup-content/corebot-popup-content.component';
import { environment } from 'src/environments/environment';
import { CoreFeature, CoreResponseCode } from 'src/app/shared/constants/enums';
import { BasicChatMessage } from 'src/app/shared/models/chat-messages/basic-chat-message';
import { UserMessage } from 'src/app/shared/models/chat-messages/user-message';
import { BotTypingMessage } from 'src/app/shared/models/chat-messages/bot-typing-message';
import { ChatBotMessage } from 'src/app/shared/models/chat-messages/chatbot-message';
import { QueryBotMessage } from 'src/app/shared/models/chat-messages/querybot-message';
import { PermissionService } from 'src/app/shared/services/permission.service';
import { CustomChatbotService } from 'src/app/shared/services/custom-chatbot.service';
import { CustomChatbot } from 'src/app/shared/models/custom-chatbot';
import { AIToolDisclaimers } from 'src/app/shared/constants/constants';

@Component({
    selector: 'app-custom-chatbot',
    templateUrl: './custom-chatbot.component.html',
    styleUrls: ['./custom-chatbot.component.scss']
})
export class CustomChatbotComponent implements OnInit, AfterViewInit, OnChanges {
    @ViewChild('chatBotInput') chatBotInput: ElementRef;
    @ViewChild('sendButton') sendButton: NbButton;
    @ViewChild('newConversationButton') newConversationButton: DxToolbarComponent;
    @ViewChild('chatContent') chatContent: CoreBotPopupContentComponent;

    @Input() customChatbot: CustomChatbot;

    @Input() message: string = '';
    @Output() onInputChange = new EventEmitter<string>();

    @Input() showPopup: boolean = false;
    @Output() showPopupChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    coreBotConversationId: string = null;
    messages: any[] = [];
    sellerName: string = '';
    request: CoreBotRequest;
    botOverride: boolean = false;
    samplePrompt: string = '';
    userHasEverybodyBotAccess: boolean = false;
    userHasAdminBotAccess: boolean = false;

    disclaimerMessage = AIToolDisclaimers.basicDisclaimer;

    constructor(
        private sellerService: SellerService,
        private customChatbotService: CustomChatbotService,
        private coreBotService: CoreBotService,
        private permissionService: PermissionService
    )
    {
        this.clearCoreBotConversation = this.clearCoreBotConversation.bind(this);
        this.openCoreBotPopout = this.openCoreBotPopout.bind(this);
    }

    ngOnInit(): void {
        this.sellerService.getMe().subscribe(seller => {
            this.sellerName = seller.name;
        });
        this.userHasEverybodyBotAccess = this.permissionService.checkCurrentUserPermission(CoreFeature.UseEverybodyCoreBotTools.toString());
        this.userHasAdminBotAccess = this.permissionService.checkCurrentUserPermission(CoreFeature.UseAdminCoreBotTools.toString());
    }

    ngAfterViewInit(): void {
        this.sendButton.disabled = true;
    }

    ngOnChanges(changes: SimpleChanges){
        if(changes['customChatbot']){
            if (this.customChatbot){
                this.messages = [
                    new BasicChatMessage(
                        `Hello! I\'m ${this.customChatbot?.name}. How can I help today?`,
                        this.customChatbot.name,
                        '../../../../assets/images/AIBot.png'
                    )
                ];
                
                this.samplePrompt = `Chat with ${this.customChatbot.name}...`
            }
        }
    }

    onContentReady(e){
        events.on(e.component.content(), 'dxmousewheel', null, (ev) => {
            ev.stopPropagation();
        });
    }

    sendMessageChatBot(e: any){
        if (this.message.length > 0){
            this.request = {
                prompt: this.message,
                customChatbotId: this.customChatbot.id,
                conversationId: this.coreBotConversationId,
                environment: environment.production ? 'production' : 'test'
            };

            this.messages.push(new UserMessage(this.sellerName, this.message));
            this.message = '';
            this.samplePrompt = `Chat with ${this.customChatbot.name}...`;
            this.disableChatBotFields();

            setTimeout(() => {
                this.messages.push(new BotTypingMessage());
            }, 400);

            this.coreBotService.getQuestionType(this.request).subscribe(async response => {
                this.coreBotConversationId = response.conversationId;
                this.request.conversationId = response.conversationId;

                await this.getChatResponse(response.response).then(result => {
                    this.enableChatBotFields();
                    if (this.botOverride === true){
                        this.botOverride = false;
                    }
                });
            });
        }
    }

    getChatResponse(bot: string){
        return new Promise<boolean>(async (res) =>{
            const overrideRegex = /^(ChatBot,|CoreBot,|QueryBot,)/i;
            const match = this.request.prompt.match(overrideRegex);
            if (match){
                this.botOverride = true;
                if(match[1].toLowerCase().replace(',', '').includes('querybot')){
                    bot = 'QueryBot';
                }
                else{
                    bot = 'ChatBot';
                }
            }

            if (bot === 'QueryBot' && this.customChatbot.isQueryBotEnabled === true && this.userHasAdminBotAccess) {
                const e: any = '';
                await this.getQueryBotResponse(e).then(x => {
                    res(true);
                });
            }
            else {
                this.customChatbotService.makeCustomChatbotRequest(this.request).subscribe(chatResponse =>{
                    this.messages.pop();
                    this.coreBotConversationId = chatResponse.conversationId;
                    this.messages.push(new ChatBotMessage(chatResponse, this.customChatbot.name));
                    res(true);
                });
            }
        });
    }

    resetChat(e: any){
        this.messages.pop();
        this.enableChatBotFields();
    }

    scrollMessagesToBottom(e: any){
        this.chatContent.nebularChat.scrollListBottom();
        document.getElementById('chatBotInput').focus();
    }

    getQueryBotResponse(e: any){
        return new Promise((res) => {
            this.coreBotService.getQueryBotResponse(this.request).subscribe(result => {
                this.messages.pop();
                this.coreBotConversationId = result.conversationId;
                setTimeout(() => {
                    if (result.responseCode === CoreResponseCode.Success){
                        const responseTable = result.response.split('<<<>>>')[0];
                        const responseQuery = result.response.split('<<<>>>')[1];
                        const explainerRequest: CoreBotRequest = {
                            prompt: responseQuery,
                            environment: environment.production ? 'production' : 'test',
                            conversationId: this.coreBotConversationId
                        };
                        const table = JSON.parse(responseTable);
                        this.messages.push(new QueryBotMessage(result, table));
                        setTimeout(() => {
                            this.messages.push(new BotTypingMessage());
                            this.coreBotService.getQueryBotExplainer(explainerRequest).subscribe(explanation => {
                                this.messages.pop();
                                setTimeout(() => {
                                    this.messages.push(new ChatBotMessage(explanation,'QueryBot', '../../../../assets/images/query-bot.png'));
                                    res(true);
                                }, 800);
                            });
                        }, 800);
                    }
                    else {
                        this.messages.push(new BasicChatMessage(result.response, 'QueryBot', '../../../../assets/images/query-bot.png'));
                        res(true);
                    }
                }, 400);
            }, error => {
                this.messages.pop();
                setTimeout(() => {
                    this.messages.push(new BasicChatMessage(
                        'An error occurred when I tried accessing my training data. Please try your prompt again.',
                        'QueryBot',
                        '../../../../assets/images/query-bot.png'
                    ));
                    this.enableChatBotFields();
                    res(true);
                }, 500);
            });
        });
    }

    onModelChange(value: string): void {
        if (value.length > 0){
            this.sendButton.disabled = false;
        }
        else {
            this.sendButton.disabled = true;
        }
        this.onInputChange.emit(value);
    }

    disableChatBotFields(){
        this.sendButton.disabled = true;
        this.chatBotInput.nativeElement.disabled = true;
        this.newConversationButton.disabled = true;
    }

    enableChatBotFields(){
        this.chatBotInput.nativeElement.disabled = false;
        this.newConversationButton.disabled = false;
    }

    clearCoreBotConversation(e: any){
        this.coreBotConversationId = null;
        this.messages = [new BasicChatMessage('How can I be of assistance?', 'CoreBot', '../../../assets/images/AIBot.png')];
    }

    onHiding(e: any){
        this.showPopup = false;
        this.showPopupChange.emit(this.showPopup);
    }

    openCoreBotPopout(e: any){
        const openerParams = [
            'fullscreen=yes',
            'height=800',
            'width=615'
        ].join(',');

        this.onHiding(e);
        const newWindow = window.open('chatbot', null, openerParams);
        newWindow.moveTo(0, 0);
    }
}
